import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { sendContactMessage } from "@/lib/contact.functions";
import { trackForm } from "@/lib/analytics";
import { FormPrivacyNotice, RequiredFieldHint } from "@/components/site/FormPrivacyNotice";

const inputClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

type FieldErrors = Partial<Record<"name" | "email" | "message", string>>;

/** UI copy per locale. `undefined` locale keeps the original English wording. */
const COPY = {
  en: {
    heading: "Send an enquiry",
    intro:
      "Describe your requirement — your message goes straight to our OXORY inbox and we reply to the email address you provide.",
    name: "Name",
    company: "Company",
    email: "Email",
    message: "Message",
    website: "Website",
    submit: "Send enquiry",
    sending: "Sending…",
    errName: "Please enter your name (at least 2 characters).",
    errEmail: "Please enter a valid email address.",
    errMessage: "Please describe your requirement in at least 10 characters.",
    toastSuccess: "Thanks — your enquiry has been sent.",
    toastError: "Sending failed. Please email info@oxory.net directly.",
    successHeading: "Thank you — your enquiry has been received.",
    successWithConfirmation:
      "Your message was delivered to our team and a confirmation copy is on its way to your email address. One of our consultants will reply within one business day.",
    successWithoutConfirmation:
      "Your message was delivered to our team. We could not send the confirmation copy to your email address, but your enquiry is safe with us and a consultant will reply within one business day.",
    reference: "Your reference:",
    addSomething: "Need to add something? Just reply to the confirmation email or write to",
    another: "Send another enquiry",
  },
  tr: {
    heading: "Talep gönderin",
    intro:
      "İhtiyacınızı anlatın — mesajınız doğrudan OXORY gelen kutumuza ulaşır ve verdiğiniz e-posta adresine yanıt veririz.",
    name: "Ad",
    company: "Şirket",
    email: "E-posta",
    message: "Mesaj",
    website: "Web sitesi",
    submit: "Talep gönder",
    sending: "Gönderiliyor…",
    errName: "Lütfen adınızı girin (en az 2 karakter).",
    errEmail: "Lütfen geçerli bir e-posta adresi girin.",
    errMessage: "Lütfen ihtiyacınızı en az 10 karakterle açıklayın.",
    toastSuccess: "Teşekkürler — talebiniz gönderildi.",
    toastError: "Gönderim başarısız oldu. Lütfen doğrudan info@oxory.net adresine yazın.",
    successHeading: "Teşekkürler — talebiniz bize ulaştı.",
    successWithConfirmation:
      "Mesajınız ekibimize iletildi ve onay kopyası e-posta adresinize gönderiliyor. Danışmanlarımızdan biri bir iş günü içinde yanıt verecek.",
    successWithoutConfirmation:
      "Mesajınız ekibimize iletildi. Onay kopyasını e-posta adresinize gönderemedik, ancak talebiniz bizde güvende ve bir danışmanımız bir iş günü içinde yanıt verecek.",
    reference: "Referans numaranız:",
    addSomething:
      "Eklemek istediğiniz bir şey mi var? Onay e-postasını yanıtlamanız yeterli; ya da şu adrese yazın:",
    another: "Yeni bir talep gönderin",
  },
} as const;

export function EnquiryForm({
  topic,
  heading,
  intro,
  messagePlaceholder,
  formId = "enquiry",
  locale,
  trackingHeading,
}: {
  topic: string;
  heading?: string;
  intro?: string;
  messagePlaceholder?: string;
  /** Stable form identifier used in conversion tracking. */
  formId?: string;
  /** Omit for the English site; "tr" renders the Turkish copy. */
  locale?: "tr";
  /** English label used in analytics when `heading` is localized. */
  trackingHeading?: string;
}) {
  const t = COPY[locale ?? "en"];
  const resolvedHeading = heading ?? t.heading;
  const resolvedIntro = intro ?? t.intro;
  const analyticsHeading = trackingHeading ?? resolvedHeading;
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [honeypot, setHoneypot] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ ticketRef?: string | undefined; confirmationSent: boolean } | null>(null);
  const send = useServerFn(sendContactMessage);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (form.name.trim().length < 2) next.name = t.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = t.errEmail;
    if (form.message.trim().length < 10) next.message = t.errMessage;
    return next;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      trackForm("invalid", formId, analyticsHeading, topic);
      return;
    }

    trackForm("submitted", formId, analyticsHeading, topic);
    setSending(true);
    try {
      const result = await send({
        data: {
          ...form,
          topic,
          honeypot,
          startedAt,
          formId,
        },
      });
      const { toast } = await import("sonner");
      if (result?.ok) {
        trackForm("success", formId, analyticsHeading, topic);
        toast.success(t.toastSuccess);
        setForm({ name: "", company: "", email: "", message: "" });
        setErrors({});
        setSent({
          ticketRef: result.ticketRef ?? undefined,
          confirmationSent: result.confirmationSent === true,
        });
      } else {
        trackForm("error", formId, analyticsHeading, topic);
        toast.error(result?.message ?? t.toastError);
      }
    } catch (error) {
      console.error("[enquiry] submit failed:", error);
      trackForm("error", formId, analyticsHeading, "network");
      (await import("sonner")).toast.error(t.toastError);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-8 shadow-card"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="size-10 text-highlight" aria-hidden="true" />
        <h2 className="mt-4 font-display text-xl font-bold">{t.successHeading}</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          {sent.confirmationSent ? t.successWithConfirmation : t.successWithoutConfirmation}
        </p>
        {sent.ticketRef && (
          <p className="mt-4 text-sm font-semibold">
            {t.reference}{" "}
            <span className="rounded bg-muted px-2 py-1 font-mono text-sm">{sent.ticketRef}</span>
          </p>
        )}
        <p className="mt-4 text-sm text-muted-foreground">
          {t.addSomething}{" "}
          <a className="font-semibold underline" href="mailto:info@oxory.net">
            info@oxory.net
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSent(null)}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-bold transition-colors hover:bg-muted"
        >
          {t.another}
        </button>
      </div>
    );
  }

  return (
    <form
      className="rounded-lg border border-border bg-card p-8 shadow-card"
      onSubmit={handleSubmit}
      noValidate
    >
      <h2 className="font-display text-xl font-bold">{resolvedHeading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{resolvedIntro}</p>
      <RequiredFieldHint locale={locale ?? "en"} />

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <label className="block text-sm font-semibold">
          {t.name} <span className="text-destructive">*</span>
          <input
            className={inputClass}
            value={form.name}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "enquiry-name-error" : undefined}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {errors.name && (
            <span id="enquiry-name-error" className="mt-1 block text-xs font-medium text-destructive">
              {errors.name}
            </span>
          )}
        </label>
        <label className="block text-sm font-semibold">
          {t.company}
          <input
            className={inputClass}
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </label>
      </div>

      <label className="mt-5 block text-sm font-semibold">
        {t.email} <span className="text-destructive">*</span>
        <input
          type="email"
          className={inputClass}
          value={form.email}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "enquiry-email-error" : undefined}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        {errors.email && (
          <span id="enquiry-email-error" className="mt-1 block text-xs font-medium text-destructive">
            {errors.email}
          </span>
        )}
      </label>

      <label className="mt-5 block text-sm font-semibold">
        {t.message} <span className="text-destructive">*</span>
        <textarea
          rows={6}
          className={inputClass}
          value={form.message}
          placeholder={messagePlaceholder}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? "enquiry-message-error" : undefined}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
        {errors.message && (
          <span
            id="enquiry-message-error"
            className="mt-1 block text-xs font-medium text-destructive"
          >
            {errors.message}
          </span>
        )}
      </label>



      {/* Honeypot — hidden from real users, bots tend to fill it in */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>
          {t.website}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <FormPrivacyNotice locale={locale ?? "en"} />

      <button
        type="submit"
        disabled={sending}
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight disabled:opacity-60"
      >
        {sending ? t.sending : t.submit}
        {sending ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
      </button>
    </form>
  );
}
